import { Input, Output, EventEmitter, Directive } from '@angular/core';
import { HubMode, Learnspace } from '@shared/models';
import { AuthenticationService } from '@shared/services/authentication.service';
import { LearnspaceEditDialogComponent } from '@app/main/learnspaces/learnspace-edit-dialog/learnspace-edit-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { switchMap, filter } from 'rxjs/operators';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Router, ActivatedRoute } from '@angular/router';
import { LearnspaceRequestAccessComponent } from '@app/main/learnspaces/learnspace-requestaccess-dialog/learnspace-requestaccess-dialog.component';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { courseLabel, spaceLabel } from '@shared/utils/wording.utils';

@Directive()
export abstract class ListOfSpacesBaseComponent {
  @Input() lsList: Learnspace[];
  @Input() title: string;
  @Input() addSeeAll: boolean;
  @Input() canCreateSpace: boolean;

  @Output() listUpdate = new EventEmitter<{shouldReloadList: boolean}>();
  @Output() seeAll = new EventEmitter();
  @Output() addLearnspace = new EventEmitter();

  public mode: HubMode;

  constructor(
    protected _courseApiService: CoursesAPIService,
    protected _authservice: AuthenticationService,
    protected _dialog: MatDialog,
    protected _router: Router,
    protected _activatedRoute: ActivatedRoute,
    protected _currentHub: CurrentHubService,
    protected _internalAnalyticsService: InternalAnalyticsService
  ) {
    this.mode = _currentHub.getHubMode();
  }

  getOwnerFullName(learnspace: Learnspace): string {
    if (learnspace.owner.last_name) {
      return `${learnspace.owner.first_name} ${learnspace.owner.last_name}`;
    }
    return learnspace.owner.first_name;
  }

  isParticipant(learnspace: Learnspace): boolean {
    return learnspace.isParticipating ||
      this._authservice.isAdmin() ||
      this._currentHub.isAdminOfCurrentHub(this._authservice.wondaUser) ||
      (learnspace.Participants && learnspace.Participants.map(p => p.id).includes(this._authservice.wondaUser.id));
  }

  isArchived(learnspace: Learnspace): boolean {
    return learnspace.status == 'archived'
  }

  getParticipantsCount(learnspace: Learnspace): number {
    if (learnspace.participantCount !== undefined) {
      return parseInt(learnspace.participantCount);
    } else if (learnspace.Participants) {
      return learnspace.Participants.length;
    } else {
      return 0;
    }
  }
  canDelete(ownerId: string): boolean {
    return ownerId === this._authservice.wondaUser.id || this._currentHub.isAdminOfCurrentHub(this._authservice.wondaUser);
  }

  canEnter(ls: Learnspace): boolean {
    return !this.isArchived(ls) && this.isParticipant(ls);
  }

  enterLearnspace(ls: Learnspace): void {
    if (this.isArchived(ls)) {
      return;
    }
    if (this.isParticipant(ls)) {
      this._router.navigate([`./${ls.id}`], { relativeTo: this._activatedRoute });
    } else {
      this.requestLearnspaceAccess(ls);
    }
  }

  contactSales(): void {
    this._internalAnalyticsService.showNewMessage();
  }

  requestLearnspaceAccess(learnspace: Learnspace): void {
    this._dialog.open(LearnspaceRequestAccessComponent, {
      width: '45vw',
      height: '50vh',
      panelClass: 'learnspace-theme',
      data: { learnspace: learnspace }
    });
  }
  updateLearnspace(ls: Learnspace): void {
    this._dialog.open(LearnspaceEditDialogComponent, {
      width: '50vw',
      panelClass: 'learnspace-theme',
      data: { learnspace: ls }
    });
  }

  deleteLearnspace(ls: Learnspace, index: number): void {
    this._dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        height: 'auto',
        panelClass: 'learnspace-theme',
        data: { title: ls.title, type: spaceLabel(ls.mode) }
      })
      .afterClosed()
      .pipe(
        filter(val => val === 'ok'),
        switchMap(a => {
          return this._courseApiService.deleteLearnspace(ls.id);
        })
      )
      .subscribe(() => {
        this.listUpdate.emit({ shouldReloadList: true });
      });
  }

  unsubscribeToLearnspace(ls: Learnspace, index: number): void {
    this._dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        height: 'auto',
        panelClass: 'learnspace-theme',
        data: {
          title: `Leave this ${spaceLabel(ls.mode)}`,
          ps: `If you have created any ${courseLabel(ls.mode, true)} in this ${spaceLabel(ls.mode)}, they will be transferred to the owner of the ${spaceLabel(ls.mode)}.`,
          type: spaceLabel(ls.mode),
          action: 'leave'
        }
      })
      .afterClosed()
      .pipe(
        filter(val => val === 'ok'),
        switchMap(a => {
          return this._courseApiService.unsubscribeLearnspace(ls.id, this._authservice.wondaUser.id);
        })
      )
      .subscribe(() => {
        this.listUpdate.emit();
        this.lsList[index].isParticipating = false; //TODO should be deleted in 'MySpaces' case
      });
  }
}
